// @flow
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import * as React from 'react'

import { colors, fonts, media } from '@toggl/style'
import { Button, Responsive } from '@toggl/ui'

import { IconCheck } from '../icons/IconCheck'
import { IconInfo } from '../icons/IconInfo'
import * as text from '../text'

type Props = {|
  +buttonText?: string,
  +buttonTooltip?: string,
  +className?: string,
  +expandButtonText: string,
  +expanded?: boolean,
  +features: $ReadOnlyArray<{|
    +text: string,
    +tooltip?: string,
  |}>,
  +onClick: () => void,
  +price: {|
    +value: string | number,
    +discountValue?: number,
    +currency?: string,
    +details: string | React.Node,
  |},
  +discount?: boolean,
  +subtitle: React.Node,
  +title: string,
  +highlighted?: boolean,
  +highlightedHeader?: string,
|}

export default function Pricing({
  buttonText,
  buttonTooltip,
  className,
  expandButtonText,
  expanded: expandedInitialValue,
  features,
  onClick,
  price,
  subtitle,
  title,
  highlighted = false,
  highlightedHeader,
  discount,
}: Props) {
  const [expanded, setExpanded] = React.useState(
    highlighted || !!expandedInitialValue
  )

  return (
    <div className={className}>
      {highlighted && (
        <HighlightedHeader>
          <div>{highlightedHeader}</div>
        </HighlightedHeader>
      )}
      <Wrapper highlighted={highlighted} expanded={expanded}>
        <Title highlighted={highlighted}>{title}</Title>
        <Subtitle highlighted={highlighted}>{subtitle}</Subtitle>
        <Price highlighted={highlighted} discount={discount} {...price} />
        {!expanded && (
          <ExpandButton caretDirection="down" onClick={() => setExpanded(true)}>
            {expandButtonText}
          </ExpandButton>
        )}
        {expanded && (
          <React.Fragment>
            <Features highlighted={highlighted} features={features} />
            <Responsive.MobileOnly>
              <MobileCtaDisclaimer highlighted={highlighted}>
                {buttonTooltip}
              </MobileCtaDisclaimer>
            </Responsive.MobileOnly>
            {buttonText && (
              <CtaButton
                text={buttonText}
                highlighted={highlighted}
                onClick={onClick}
                tooltip={buttonTooltip}
              />
            )}
          </React.Fragment>
        )}
      </Wrapper>
    </div>
  )
}

const HighlightedHeader: React.ComponentType<any> = styled.div`
  font-size: 1.1rem;
  color: ${colors.beige};
  height: 45px;
  width: 100%;
  background-color: ${({ theme }) => theme.primary};
  div {
    padding-top: 7px;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  ${media.mq[1]} {
    max-width: 500px;
  }
  ${media.mq[2]} {
    max-width: 280px;
  }
`

const Wrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  padding: 30px;
  width: 100%;
  background-color: ${({ highlighted }) =>
    highlighted ? colors.almostWhite : colors.fadedPurple};

  ${media.mq[1]} {
    ${({ expanded }) => (expanded ? 'min-height: 830px;' : '')};
  }

  ${({ highlighted, theme }) =>
    highlighted ? `box-shadow: inset 0 0 0 7px ${theme.primary};` : ''};

  ${media.mq[2]} {
    margin-top: ${({ highlighted }) => (highlighted ? 0 : '50px')};
  }
`

const Title: React.ComponentType<any> = styled(text.H4)`
  color: ${({ highlighted, theme }) =>
    highlighted ? theme.primary : theme.accent};

  margin-bottom: 15px;
`

const Subtitle: React.ComponentType<any> = styled(text.P3)`
  color: ${colors.darkPurple};
  margin-bottom: 25px;
  color: ${({ highlighted, theme }) =>
    highlighted ? colors.darkPurple : theme.accent} !important;
`

const Price = ({
  currency,
  value,
  details,
  discountValue,
  discount,
  highlighted,
}) => {
  if (!discountValue || typeof value !== 'number') {
    return (
      <PriceWrapper highlighted={highlighted}>
        <div>{`${currency || ''}${value}`}</div>
        <div>{details}</div>
      </PriceWrapper>
    )
  }

  return (
    <PriceWrapper highlighted={highlighted}>
      <div>
        {`${currency || ''}`}
        {discountValue && discount ? (
          <span>{discountValue}</span>
        ) : (
          <span>{value}</span>
        )}
      </div>
      <div>{details}</div>
    </PriceWrapper>
  )
}

const PriceWrapper: React.ComponentType<any> = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  & > div:nth-of-type(1) {
    ${text.heading4};
    color: ${({ theme }) => theme.primary};
    font-size: 48px;
    line-height: 54px;
    margin-right: 20px;
  }

  & > div:last-child {
    ${text.paragraph4};
    font-size: 12px;
    line-height: 18px;
    width: 120px;
    color: ${({ highlighted, theme }) =>
      highlighted ? colors.darkPurple : theme.accent};
  }

  padding-bottom: 25px;
  margin-bottom: 25px;
  border-bottom: 1px solid ${colors.fadedPurpleAlt};
`

const ExpandButton = styled(Button.Secondary)`
  ${text.paragraph4};

  display: flex;
  justify-content: center;
  cursor: pointer;

  color: ${({ theme }) => theme.accent};
`

const Features = ({ features, highlighted }) => (
  <FeaturesWrapper highlighted={highlighted}>
    {features.map((feature, i) => (
      <li key={i}>
        <IconCheck />
        {feature.text}
        {feature.tooltip && (
          <span data-title={feature.tooltip}>
            <IconInfo color={colors.lightPurple} />
          </span>
        )}
      </li>
    ))}
  </FeaturesWrapper>
)

const tooltipStyles = css`
  position: relative;
  cursor: pointer;
  &:before,
  &:after {
    opacity: 0;
    visibility: hidden;
    transition: 0.15s ease;
    left: 50%;
    position: absolute;
    z-index: 10;
  }
  &:before {
    content: '';
    height: 0;
    width: 0;
    border-style: solid;
    border-color: ${colors.pink70} transparent transparent transparent;
    border-width: 8px 8px 0 8px;
    bottom: calc(100% + 2px);
    transform: translateX(-50%);
  }
  &:after {
    content: attr(data-title);
    transform: translateX(-90%);
    background: ${colors.pink70};
    font-family: ${fonts.inter};
    ${text.paragraph4};
    color: ${colors.darkPurple};
    padding: 15px 20px;
    bottom: calc(100% + 8px);
    width: 250px;
  }
  &:hover {
    &:before,
    &:after {
      visibility: visible;
      opacity: 1;
    }
    svg {
      fill: ${colors.purple60};
    }
  }
  ${media.mq[2]} {
    &:after {
      transform: translateX(-50%);
    }
  }
`

const buttonStyles = css`
  justify-content: center;
  margin-top: 25px;
  ${media.mq[1]} {
    margin-top: auto;
  }
`

const FeaturesWrapper: React.ComponentType<any> = styled.ul`
  ${text.paragraph4};
  color: ${({ highlighted, theme }) =>
    highlighted ? colors.darkPurple : theme.accent};
  list-style: none;
  margin: 0 0 25px 0;
  padding: 0;

  li {
    display: flex;
    line-height: 1.3;
    column-gap: 15px;
    position: relative;
    svg:first-of-type {
      transform: translateY(3px);
      flex: 0 0 18px;
    }
    span[data-title] {
      ${tooltipStyles};
      margin-left: auto;
      align-self: center;
      svg {
        transform: translateY(-2px);
      }
    }
  }

  li:not(:last-child) {
    margin-bottom: 15px;
  }
`

const MobileCtaDisclaimer = styled(text.P4)`
  text-align: center;
  margin-top: 10px;
  color: ${({ highlighted }) =>
    highlighted ? colors.fadedPurple : colors.beige};
`

const EnhancedPrimaryButton = styled(Button.Primary)`
  ${buttonStyles};
  ${tooltipStyles};
  &:after {
    transform: translateX(-50%) !important;
  }
  margin-top: 45px;
`

const EnhancedAlternateButton = styled(Button.Alternate)`
  ${buttonStyles};
  ${tooltipStyles};
  &:after {
    transform: translateX(-50%) !important;
  }
`

const CtaButton = ({ onClick, text, highlighted, tooltip }) =>
  highlighted ? (
    <EnhancedPrimaryButton fluid onClick={onClick} data-title={tooltip}>
      {text}
    </EnhancedPrimaryButton>
  ) : (
    <EnhancedAlternateButton fluid onClick={onClick} data-title={tooltip}>
      {text}
    </EnhancedAlternateButton>
  )
